import { useState } from "react";
import { toast } from "sonner";
import {
  DownloadSimple,
  FolderOpen,
  PencilSimple,
  UploadSimple,
  Trash,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

export default function ResourceLibrary({ category = "other" }) {
  const { data, loading, refetch } = useGet("/staff/resource-library");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", form_category: "", sign_required: false });
  const [file, setFile] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const items = (data || []).filter((item) => item.category === category);
  const isForms = category === "form";
  const destination = isForms ? "Forms" : "Other resources";

  const openCreate = () => {
    setEditing(null);
    setForm({ title: "", description: "", form_category: "", sign_required: false });
    setFile(null);
    setOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      title: item.title || "",
      description: item.description || "",
      form_category: item.form_category || "",
      sign_required: !!item.sign_required,
    });
    setFile(null);
    setOpen(true);
  };

  const upload = async () => {
    if (!form.title.trim() || (!editing && !file)) {
      toast.error("Title and file are required.");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/staff/resource-library/${editing.id}`, { ...form });
        toast.success("Resource details updated.");
      } else {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("title", form.title);
        fd.append("description", form.description);
        fd.append("category", category);
        fd.append("form_category", form.form_category || "");
        fd.append("sign_required", form.sign_required ? "true" : "false");
        await api.post("/staff/resource-library", fd);
        toast.success(isForms ? "Form published." : "Resource published.");
      }
      setOpen(false);
      refetch();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const replace = async (item, event) => {
    const replacement = event.target.files?.[0];
    event.target.value = "";
    if (!replacement) return;
    const fd = new FormData();
    fd.append("file", replacement);
    try {
      await api.post(`/staff/resource-library/${item.id}/replace`, fd);
      toast.success("Resource file replaced.");
      refetch();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const remove = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/staff/resource-library/${deleting.id}`);
      toast.success("Resource removed from portal access.");
      setDeleting(null);
      refetch();
    } catch (error) {
      toast.error(errMsg(error));
    }
  };
  const download = async (item) => {
    try {
      const response = await api.get(
        `/staff/resource-library/${item.id}/file`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.title || "resource";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  if (loading) return <Loading label="Loading resource library" />;

  return (
    <div>
      <PageHeader
        eyebrow="Gledswood Operations"
        title={destination}
        description={
          isForms
            ? "Forms you publish here appear on every event-connected client's Forms page, ready to download and sign. Signed copies come back to events@gledswood.com.au."
            : "Global venue documents published to every event-connected client."
        }
      >
        <Button
          className="rounded-full bg-primary hover:bg-primary/90"
          onClick={openCreate}
          data-testid="add-library-btn"
        >
          <UploadSimple size={18} className="mr-1" />{" "}
          {isForms ? "Upload form" : "Publish resource"}
        </Button>
      </PageHeader>

      {items.length === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title={isForms ? "No forms uploaded" : "No resources published"}
          description={
            isForms
              ? "Upload a PDF with a title, category (e.g. Compliance or Payment) and description. Clients download and sign it from their Forms page."
              : "Publish a venue document with a title, description and file."
          }
          action={
            <Button
              className="rounded-full bg-primary hover:bg-primary/90"
              onClick={openCreate}
              data-testid="empty-add-library-btn"
            >
              <UploadSimple size={18} className="mr-1" />{" "}
              {isForms ? "Upload form" : "Publish resource"}
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((r) => (
            <div
              key={r.id}
              data-testid={`library-${r.id}`}
              className="flex items-start justify-between gap-4 rounded-lg border border-border bg-card p-6"
            >
              <div className="min-w-0">
                <Badge variant="outline" className="rounded-full mb-2">
                  {isForms
                    ? r.form_category || "Form"
                    : r.legacy_scope
                      ? "Legacy scoped"
                      : "Global"}
                </Badge>
                {isForms && r.sign_required && (
                  <Badge
                    variant="outline"
                    className="rounded-full mb-2 ml-2 border-accent/40 text-accent"
                    data-testid={`library-sign-required-${r.id}`}
                  >
                    Sign required
                  </Badge>
                )}
                <h3 className="font-heading text-xl text-primary">{r.title}</h3>
                {r.original_filename && (
                  <p className="mt-1 truncate text-xs text-muted-foreground">
                    {r.original_filename}
                  </p>
                )}
                {r.description && (
                  <p
                    className="mt-2 text-sm text-muted-foreground"
                    data-testid={`library-description-${r.id}`}
                  >
                    {r.description}
                  </p>
                )}
                {r.legacy_scope && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Retained for migration review. New library uploads are
                    global only.
                  </p>
                )}
              </div>
              <div className="flex shrink-0 gap-1">
                <button
                  onClick={() => download(r)}
                  data-testid={`download-library-${r.id}`}
                  className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-primary"
                >
                  <DownloadSimple size={18} />
                </button>
                <label className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-primary cursor-pointer">
                  <input
                    type="file"
                    className="sr-only"
                    accept=".pdf,.docx,.xlsx,.csv,.png,.jpg,.jpeg,.webp"
                    onChange={(event) => replace(r, event)}
                    data-testid={`replace-library-${r.id}`}
                  />
                  <UploadSimple size={18} />
                </label>
                <button
                  onClick={() => openEdit(r)}
                  data-testid={`edit-library-${r.id}`}
                  className="p-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-primary"
                >
                  <PencilSimple size={18} />
                </button>
                <button
                  onClick={() => setDeleting(r)}
                  data-testid={`delete-library-${r.id}`}
                  className="p-2 rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                  aria-label={`Remove ${r.title} from portal access`}
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">
              {editing ? "Edit resource" : "Publish resource"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Update this global venue resource."
                : "Upload a venue document for every event-connected client."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="eyebrow">Title</Label>
              <Input
                className="mt-2"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                data-testid="library-title-input"
              />
            </div>
            <div>
              <Label className="eyebrow">Description</Label>
              <Textarea
                className="mt-2"
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="What is this document for?"
                data-testid="library-description-input"
              />
            </div>
            {isForms && (
              <>
                <div>
                  <Label className="eyebrow">Form category</Label>
                  <Input
                    className="mt-2"
                    value={form.form_category}
                    onChange={(e) => setForm({ ...form, form_category: e.target.value })}
                    placeholder="e.g. Compliance or Payment"
                    data-testid="library-form-category-input"
                  />
                </div>
                <label className="flex items-center gap-3 text-sm text-primary">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-border"
                    checked={form.sign_required}
                    onChange={(e) => setForm({ ...form, sign_required: e.target.checked })}
                    data-testid="library-sign-required-input"
                  />
                  Clients must sign and return this form
                </label>
              </>
            )}
            {!editing && (
              <div>
                <Label className="eyebrow">File</Label>
                <Input
                  type="file"
                  accept=".pdf,.docx,.xlsx,.csv,.png,.jpg,.jpeg,.webp"
                  className="mt-2"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  data-testid="library-file-input"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="rounded-full bg-primary hover:bg-primary/90"
              onClick={upload}
              disabled={busy}
              data-testid="save-library-btn"
            >
              {busy ? "Saving…" : editing ? "Save details" : "Publish"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!deleting} onOpenChange={(value) => !value && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light">
              Remove this resource from portal access?
            </DialogTitle>
            <DialogDescription>
              This logically removes the resource from client and staff lists. The stored file is not
              claimed to be physically deleted.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              className="rounded-full bg-destructive hover:bg-destructive/90"
              onClick={remove}
              data-testid="confirm-delete-library"
            >
              Remove access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
